import React, { useEffect, useState, useRef } from "react";
import {
  Loader2,
  Trash2,
  CheckCircle2,
  AlertCircle,
  Megaphone,
  Power,
  Plus,
  UploadCloud,
  FileText,
  Eye,
  Download,
  Clock,
} from "lucide-react";
import { API, resolveImage } from "../products";

const TOKEN_KEY = "tcs_admin_token";
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` });

function Toast({ toast, onDone }) {
  useEffect(() => {
    if (!toast) return;
    const id = setTimeout(onDone, 2600);
    return () => clearTimeout(id);
  }, [toast, onDone]);
  if (!toast) return null;
  const ok = toast.type !== "error";
  return (
    <div
      data-testid="popup-toast"
      className={`fixed bottom-6 right-6 z-[200] flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg text-white font-semibold ${ok ? "bg-green-600" : "bg-red-600"}`}
    >
      {ok ? <CheckCircle2 className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
      {toast.msg}
    </div>
  );
}

const EngagementPanel = ({ s }) => {
  if (!s || !s.impressions) {
    return (
      <div className="mt-3 rounded-xl bg-slate-50 border border-slate-100 px-3 py-2 text-xs text-slate-400" data-testid="popup-engagement-empty">
        No engagement data yet — go live to start measuring.
      </div>
    );
  }
  const Metric = ({ icon: Icon, label, value, testid }) => (
    <div className="flex flex-col items-center text-center" data-testid={testid}>
      <Icon className="w-4 h-4 text-green-600 mb-0.5" />
      <span className="font-extrabold text-slate-800 leading-none">{value}</span>
      <span className="text-[10px] text-slate-400 mt-0.5">{label}</span>
    </div>
  );
  return (
    <div className="mt-3 rounded-xl bg-slate-50 border border-slate-100 p-3" data-testid="popup-engagement">
      <div className="grid grid-cols-4 gap-2">
        <Metric icon={Eye} label="Views" value={s.impressions} testid="popup-stat-impressions" />
        <Metric icon={Download} label="Downloads" value={s.downloads} testid="popup-stat-downloads" />
        <Metric icon={CheckCircle2} label="DL rate" value={`${s.download_rate}%`} testid="popup-stat-rate" />
        <Metric icon={Clock} label="Avg view" value={`${s.avg_dwell}s`} testid="popup-stat-dwell" />
      </div>
      <div className="mt-2 pt-2 border-t border-slate-200 flex items-center justify-between gap-2 text-[11px] text-slate-500 flex-wrap">
        <span>EN: {s.by_lang.en.impressions} views · {s.by_lang.en.downloads} dl</span>
        <span>ES: {s.by_lang.es.impressions} views · {s.by_lang.es.downloads} dl</span>
        <span className="text-slate-400">closed: {s.close_x} X · {s.close_overlay} outside</span>
      </div>
    </div>
  );
};

const DropZone = ({ label, lang, bannerId, image, onUpload }) => {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);

  const handle = async (file) => {
    if (!file) return;
    setBusy(true);
    await onUpload(lang, file);
    setBusy(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div className="flex-1 min-w-0">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs font-bold uppercase tracking-wide text-slate-500">{label}</span>
        {image && <span className="text-[10px] font-bold text-green-600">✓ set</span>}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*,application/pdf"
        className="hidden"
        onChange={(e) => handle(e.target.files?.[0])}
        data-testid={`popup-file-${lang}-${bannerId}`}
      />
      <div
        onClick={() => !busy && inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          handle(e.dataTransfer.files?.[0]);
        }}
        data-testid={`popup-dropzone-${lang}-${bannerId}`}
        className={`relative cursor-pointer rounded-xl border-2 border-dashed aspect-video flex items-center justify-center overflow-hidden transition-colors ${
          dragging ? "border-green-500 bg-green-50" : "border-slate-300 bg-slate-50 hover:border-green-400"
        }`}
      >
        {image ? (
          <img src={resolveImage(image)} alt={label} className="w-full h-full object-contain" />
        ) : (
          <div className="text-center px-3 text-slate-400">
            <UploadCloud className="w-6 h-6 mx-auto mb-1" />
            <p className="text-xs font-medium">Drag &amp; drop or click</p>
            <p className="text-[10px] mt-0.5 flex items-center justify-center gap-1"><FileText className="w-3 h-3" /> image or PDF</p>
          </div>
        )}
        {busy && (
          <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-green-600" />
          </div>
        )}
      </div>
    </div>
  );
};

export default function AdminPopups() {
  const [enabled, setEnabled] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [banners, setBanners] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [toast, setToast] = useState(null);
  const notify = (type, msg) => setToast({ type, msg });

  const load = async () => {
    setLoading(true);
    try {
      const [res, sres] = await Promise.all([
        fetch(`${API}/admin/popups`, { headers: authHeaders() }),
        fetch(`${API}/admin/popups/stats`, { headers: authHeaders() }),
      ]);
      const data = await res.json();
      setEnabled(!!data.enabled);
      setActiveId(data.active_id || null);
      setBanners(Array.isArray(data.banners) ? data.banners : []);
      if (sres.ok) setStats(await sres.json());
    } catch (e) {
      notify("error", "Failed to load popups");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const addBanner = async () => {
    setAdding(true);
    try {
      const res = await fetch(`${API}/admin/popups`, { method: "POST", headers: authHeaders() });
      if (!res.ok) throw new Error("add");
      const banner = await res.json();
      setBanners((prev) => [banner, ...prev]);
      notify("ok", "Banner created — add English/Spanish images");
    } catch (e) {
      notify("error", "Could not create banner");
    } finally {
      setAdding(false);
    }
  };

  const uploadImage = async (bannerId, lang, file) => {
    const fd = new FormData();
    fd.append("lang", lang);
    fd.append("file", file);
    try {
      const res = await fetch(`${API}/admin/popups/${bannerId}/image`, { method: "POST", headers: authHeaders(), body: fd });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "upload");
      }
      const updated = await res.json();
      setBanners((prev) => prev.map((b) => (b.id === bannerId ? updated : b)));
      notify("ok", `${lang === "es" ? "Spanish" : "English"} image saved`);
    } catch (e) {
      notify("error", typeof e.message === "string" && e.message !== "upload" ? e.message : "Upload failed");
    }
  };

  const applyBanner = async (banner) => {
    if (!banner.image_en && !banner.image_es) {
      notify("error", "Add at least one image before applying");
      return;
    }
    try {
      const res = await fetch(`${API}/admin/popups/${banner.id}/apply`, { method: "POST", headers: authHeaders() });
      if (!res.ok) throw new Error("apply");
      const data = await res.json();
      setActiveId(data.active_id);
      setEnabled(!!data.enabled);
      notify("ok", "This banner is now live");
    } catch (e) {
      notify("error", "Could not apply banner");
    }
  };

  const toggle = async () => {
    const next = !enabled;
    try {
      const res = await fetch(`${API}/admin/popups/toggle`, {
        method: "POST",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ enabled: next }),
      });
      if (!res.ok) throw new Error("toggle");
      setEnabled(next);
      notify("ok", next ? "Popup is ON — visitors will see it" : "Popup is OFF");
    } catch (e) {
      notify("error", "Could not update toggle");
    }
  };

  const remove = async (banner) => {
    if (!window.confirm("Delete this banner? This cannot be undone.")) return;
    try {
      const res = await fetch(`${API}/admin/popups/${banner.id}`, { method: "DELETE", headers: authHeaders() });
      if (!res.ok) throw new Error("delete");
      setBanners((prev) => prev.filter((b) => b.id !== banner.id));
      if (activeId === banner.id) {
        setActiveId(null);
        setEnabled(false);
      }
      notify("ok", "Banner deleted");
    } catch (e) {
      notify("error", "Delete failed");
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-popups">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Megaphone className="w-5 h-5 text-green-600" />
          <div>
            <h2 className="font-extrabold text-slate-800 text-lg">Homepage Popup</h2>
            <p className="text-sm text-slate-500">
              Create banners with English &amp; Spanish images (upload an image or a PDF), choose one to go live. Shown once per visitor session on the homepage.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={addBanner}
            disabled={adding}
            data-testid="popup-add-banner"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-green-600 hover:bg-green-700 text-white font-bold text-sm disabled:opacity-60"
          >
            {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} New banner
          </button>
          <div className="flex items-center gap-2.5" title={!activeId ? "Apply a banner first" : ""}>
            <span className="text-sm font-bold text-slate-600 hidden sm:inline">Popup</span>
            <button
              type="button"
              role="switch"
              aria-checked={enabled}
              onClick={toggle}
              data-testid="popup-toggle"
              disabled={!activeId}
              className={`relative inline-flex items-center h-9 w-[92px] rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                enabled ? "bg-green-600" : "bg-slate-300"
              }`}
            >
              <span
                className={`absolute text-xs font-extrabold uppercase tracking-wide transition-all ${
                  enabled ? "left-3 text-white" : "right-3 text-slate-600"
                }`}
              >
                {enabled ? "On" : "Off"}
              </span>
              <span
                className={`absolute top-1 h-7 w-7 rounded-full bg-white shadow-md flex items-center justify-center transition-all duration-200 ${
                  enabled ? "left-[59px]" : "left-1"
                }`}
              >
                <Power className={`w-3.5 h-3.5 ${enabled ? "text-green-600" : "text-slate-400"}`} />
              </span>
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-slate-400">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…
        </div>
      ) : banners.length === 0 ? (
        <div className="text-center py-16 text-slate-400 rounded-2xl border-2 border-dashed border-slate-300" data-testid="popup-empty">
          No banners yet. Click "New banner" to create one.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {banners.map((b) => {
            const isLive = activeId === b.id;
            return (
              <div
                key={b.id}
                data-testid={`popup-banner-${b.id}`}
                className={`rounded-2xl border bg-white p-4 ${isLive ? "border-green-500 ring-2 ring-green-500/30" : "border-slate-200"}`}
              >
                <div className="flex items-center justify-between mb-3">
                  {isLive ? (
                    <span data-testid={`popup-live-badge-${b.id}`} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-green-600 text-white text-xs font-bold">
                      {enabled ? "● LIVE" : "SELECTED (off)"}
                    </span>
                  ) : <span className="text-xs text-slate-400">Banner</span>}
                  <button
                    onClick={() => remove(b)}
                    data-testid={`popup-delete-${b.id}`}
                    className="inline-flex items-center justify-center w-8 h-8 rounded-lg border border-red-200 text-red-600 hover:bg-red-50"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex gap-3">
                  <DropZone label="English" lang="en" bannerId={b.id} image={b.image_en} onUpload={(lang, file) => uploadImage(b.id, lang, file)} />
                  <DropZone label="Español" lang="es" bannerId={b.id} image={b.image_es} onUpload={(lang, file) => uploadImage(b.id, lang, file)} />
                </div>
                <button
                  onClick={() => applyBanner(b)}
                  disabled={isLive}
                  data-testid={`popup-apply-${b.id}`}
                  className="mt-3 w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white font-bold text-sm disabled:opacity-50 disabled:cursor-default"
                >
                  {isLive ? <><CheckCircle2 className="w-4 h-4" /> Applied</> : "Apply (make live)"}
                </button>
                <EngagementPanel s={stats[b.id]} />
              </div>
            );
          })}
        </div>
      )}

      <Toast toast={toast} onDone={() => setToast(null)} />
    </div>
  );
}
